import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { MessageSquare, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fullImageUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${BACKEND_URL}${u}`;
};

const CATEGORIES = ["Tous", "Parfum", "Accessoire téléphone", "Bracelet", "Lunettes", "Écouteurs", "Montre", "Autre"];

export default function Products() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("Tous");
  const [selected, setSelected] = useState(null);
  const [reserveMsg, setReserveMsg] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "Tous") return products;
    return products.filter((p) => p.category === filter);
  }, [products, filter]);

  const closeModal = () => { setSelected(null); setReserveMsg(""); };

  const reserve = async () => {
    if (!user) {
      toast.message("Connectez-vous pour réserver ce produit");
      nav("/login");
      return;
    }
    if (!selected) return;
    setSending(true);
    try {
      await api.post("/messages", { type: "reservation", message: reserveMsg || `Réservation : ${selected.name}`, product_id: selected.id });
      toast.success("Réservation envoyée à la boutique");
      closeModal();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="products-page">
      <div className="fade-up">
        <div className="label-eyebrow mb-3">Catalogue Noctalis</div>
        <h1 className="font-display text-5xl lg:text-6xl mb-3">Nos produits</h1>
        <p className="text-[#a3a8a4] max-w-2xl">Parfums, lunettes, bracelets et accessoires sélectionnés à la main. Consultez le stock en temps réel — les achats se font en boutique.</p>
      </div>

      <div className="flex flex-wrap gap-2 mt-10 mb-8 fade-up-delay-1">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            data-testid={`filter-${c.replace(/\s+/g,'-').toLowerCase()}`}
            className={`px-5 py-2 text-xs uppercase tracking-widest border transition-colors ${filter === c ? "border-[#d4af37] text-[#d4af37]" : "border-[rgba(253,251,247,0.1)] text-[#a3a8a4] hover:text-[#fdfbf7]"}`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 fade-up-delay-2">
        {filtered.length === 0 && <div className="col-span-full text-[#a3a8a4] py-12 text-center">Aucun produit dans cette catégorie.</div>}
        {filtered.map((p) => (
          <div key={p.id} className="product-card group cursor-pointer" onClick={() => setSelected(p)} data-testid={`product-${p.id}`}>
            <div className="aspect-[4/5] overflow-hidden bg-[#0a0e0b] mb-4 relative">
              {p.image_url ? (
                <img src={fullImageUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover product-img"/>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#a3a8a4]">Aucune image</div>
              )}
              <div className="absolute top-3 right-3 px-3 py-1 text-[10px] tracking-widest uppercase bg-[#0a0e0b]/80 backdrop-blur-sm border border-[rgba(253,251,247,0.1)]">
                {p.stock > 0 ? <span className="gold-text">En stock · {p.stock}</span> : <span className="text-red-400">Épuisé</span>}
              </div>
            </div>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display text-xl">{p.name}</div>
                <div className="text-xs text-[#a3a8a4] mt-1">{p.category}</div>
                {p.price > 0 && <div className="text-base mt-1">{p.price.toFixed(2)} €</div>}
                {p.points_reward > 0 && <div className="text-xs gold-text mt-1">+{p.points_reward} pts à l'achat</div>}
              </div>
              <button className="text-xs label-eyebrow hover:text-[#e6c27a]" data-testid={`view-details-${p.id}`}>Voir détails →</button>
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={closeModal} data-testid="product-modal">
          <div className="max-w-3xl w-full bg-[#0a0e0b] border border-[rgba(253,251,247,0.08)] grid grid-cols-1 md:grid-cols-2 max-h-[90vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
            <div className="aspect-[4/5] overflow-hidden bg-black">
              {selected.image_url && <img src={fullImageUrl(selected.image_url)} alt={selected.name} className="w-full h-full object-cover"/>}
            </div>
            <div className="p-8 flex flex-col">
              <div className="label-eyebrow mb-3">{selected.category}</div>
              <h3 className="font-display text-4xl mb-3">{selected.name}</h3>
              {selected.price > 0 && <div className="font-display text-2xl gold-text mb-3">{selected.price.toFixed(2)} €</div>}
              <p className="text-[#a3a8a4] leading-relaxed mb-6 flex-1">{selected.description || "Détails à venir."}</p>
              <div className="border-t border-[rgba(253,251,247,0.08)] pt-4 flex items-center justify-between mb-4">
                <span className="text-sm text-[#a3a8a4]">Disponibilité</span>
                <span className={selected.stock > 0 ? "gold-text" : "text-red-400"}>{selected.stock > 0 ? `En stock · ${selected.stock}` : "Épuisé"}</span>
              </div>
              {selected.points_reward > 0 && (
                <div className="flex items-center justify-between mb-4 border border-[#d4af37]/30 bg-[#d4af37]/5 px-4 py-3">
                  <span className="text-sm">Récompense fidélité</span>
                  <span className="gold-text font-medium">+{selected.points_reward} pts à l'achat</span>
                </div>
              )}

              {selected.stock > 0 && (
                <div className="space-y-3 mb-4">
                  <div className="label-eyebrow flex items-center gap-2"><MessageSquare size={12}/> Réserver ce produit</div>
                  {user ? (
                    <>
                      <textarea
                        value={reserveMsg}
                        onChange={(e)=>setReserveMsg(e.target.value)}
                        placeholder="Un message pour le vendeur (taille, parfum souhaité, etc.) — optionnel"
                        rows={3}
                        className="w-full bg-[#050706] border border-[rgba(253,251,247,0.1)] px-3 py-2 text-sm focus:border-[#d4af37] focus:outline-none"
                        data-testid="reserve-message"
                      />
                      <button onClick={reserve} disabled={sending} className="w-full bg-[#d4af37] text-black px-5 py-3 text-xs uppercase tracking-widest hover:bg-[#e6c27a] disabled:opacity-50" data-testid="reserve-btn">
                        {sending ? "Envoi..." : "Envoyer ma réservation"}
                      </button>
                    </>
                  ) : (
                    <div className="border border-[#d4af37]/30 bg-[#1a241b]/30 p-4 space-y-3">
                      <div className="text-sm text-[#a3a8a4] flex items-center gap-2"><Lock size={14}/> Connectez-vous pour réserver ce produit</div>
                      <div className="flex gap-2">
                        <button onClick={() => nav("/login")} className="flex-1 bg-[#d4af37] text-black px-4 py-2.5 text-xs uppercase tracking-widest hover:bg-[#e6c27a]" data-testid="login-to-reserve">Se connecter</button>
                        <button onClick={() => nav("/register")} className="flex-1 border border-[#d4af37] text-[#d4af37] px-4 py-2.5 text-xs uppercase tracking-widest hover:bg-[#d4af37]/10" data-testid="register-to-reserve">S'inscrire</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="text-xs text-[#a3a8a4] italic">Achat / retrait en boutique uniquement</div>
              <button onClick={closeModal} className="mt-4 border border-[rgba(253,251,247,0.1)] px-6 py-2.5 text-xs uppercase tracking-widest hover:border-[#d4af37]" data-testid="close-modal">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
